import React, { useState } from 'react';
import { Check, AlertTriangle } from 'react-feather';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../context/ApiContext';
import toast from 'react-hot-toast';
import { calculatePasswordStrength } from '../../utils/passwordStrength';
import styles from './CreateSecretPage.module.css';

const CreateSecretPage = () => {
  const { isAuthenticated } = useAuth();
  const { createSecret, generatePassword } = useApi();

  const [formData, setFormData] = useState({
    secret: '',
    ttl: 3600, // Default 1 hour
    passphrase: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Password generator state
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatorOptions, setGeneratorOptions] = useState({
    length: 20,
    include_lowercase: true,
    include_uppercase: true,
    include_numbers: true,
    include_symbols: true,
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [generatingPassword, setGeneratingPassword] = useState(false);

  // Passphrase visibility toggle
  const [showPassphrase, setShowPassphrase] = useState(false);

  // HANDLE form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // SUBMIT secret creation form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // CREATE secret via API
      const secretData = {
        secret: formData.secret.trim(),
        ttl: parseInt(formData.ttl),
      };

      // ADD passphrase if provided
      if (formData.passphrase.trim()) {
        secretData.passphrase = formData.passphrase.trim();
      }

      const response = await createSecret(secretData);

      toast.success('Secret erfolgreich erstellt!');
      setResult(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // COPY secret URL to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('In Zwischenablage kopiert!');
    } catch (error) {
      toast.error('Fehler beim Kopieren in die Zwischenablage');
    }
  };

  // GET available TTL options based on auth status
  const getTTLOptions = () => {
    const baseOptions = [
      { value: 300, label: '5 Minuten' },
      { value: 900, label: '15 Minuten' },
      { value: 1800, label: '30 Minuten' },
      { value: 3600, label: '1 Stunde' },
    ];

    if (isAuthenticated) {
      return [
        ...baseOptions,
        { value: 21600, label: '6 Stunden' },
        { value: 86400, label: '1 Tag' },
        { value: 259200, label: '3 Tage' },
        { value: 604800, label: '7 Tage' },
      ];
    }

    return baseOptions;
  };

  // HANDLE password generator option changes
  const handleGeneratorChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGeneratorOptions(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value),
    }));
  };

  // GENERATE password via API
  const handleGeneratePassword = async () => {
    // Validate at least one character type is selected
    const { include_lowercase, include_uppercase, include_numbers, include_symbols } = generatorOptions;
    if (!include_lowercase && !include_uppercase && !include_numbers && !include_symbols) {
      toast.error('Bitte wähle mindestens einen Zeichensatz');
      return;
    }

    setGeneratingPassword(true);
    try {
      const response = await generatePassword(generatorOptions);
      setGeneratedPassword(response.password);
      toast.success('Passwort generiert!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGeneratingPassword(false);
    }
  };

  // USE generated password in secret field
  const useGeneratedPassword = () => {
    setFormData(prev => ({
      ...prev,
      secret: generatedPassword,
    }));
    toast.success('Passwort zum Secret hinzugefügt');
  };

  // USE generated password as passphrase
  const useAsPassphrase = () => {
    setFormData(prev => ({
      ...prev,
      passphrase: generatedPassword,
    }));
    toast.success('Passwort als zusätzliches Passwort festgelegt');
  };

  // CALCULATE password strength for display
  const getPasswordStrength = () => {
    if (!generatedPassword) return null;

    const charTypes = {
      lowercase: generatorOptions.include_lowercase,
      uppercase: generatorOptions.include_uppercase,
      numbers: generatorOptions.include_numbers,
      symbols: generatorOptions.include_symbols,
    };

    return calculatePasswordStrength(generatorOptions.length, charTypes);
  };

  if (result) {
    return (
      <div className="container">
        <div className={styles.success}>
          <h1>
            <Check size={32} className={styles.successIcon} /> Secret erstellt
          </h1>
          <p>Dein Secret wurde erstellt und kann einmalig über den folgenden Link zugegriffen werden:</p>

          <div className={styles.resultBox}>
            <label className="form-label">Secret-URL:</label>
            <div className={styles.urlBox}>
              <input
                type="text"
                value={result.url}
                readOnly
                className="form-input"
              />
              <button
                onClick={() => copyToClipboard(result.url)}
                className="btn btn-secondary"
              >
                Kopieren
              </button>
            </div>
          </div>

          <div className={styles.info}>
            <p><strong>Ablauf:</strong> {new Date(result.expires_at).toLocaleString()}</p>
            <p>
              <strong style={{ color: 'var(--secondary-color)' }}>Wichtig:</strong> Dieses Secret wird nach dem ersten Zugriff zerstört.
            </p>
          </div>

          <button
            onClick={() => {
              setResult(null);
              setFormData({ secret: '', ttl: 3600, passphrase: '' });
            }}
            className="btn btn-primary"
          >
            Neues Secret erstellen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1 className="mb-2">Neues Secret erstellen</h1>

        {!isAuthenticated && (
          <div className={styles.warning}>
            <strong>Anonymer Modus:</strong> Secrets sind maximal 1 Stunde verfügbar.
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className="form-group">
            <label htmlFor="secret" className="form-label">
              Inhalt *
            </label>
            <textarea
              id="secret"
              name="secret"
              value={formData.secret}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Gib deinen Inhalt, Passwort oder sensible Informationen ein..."
              required
              maxLength={65536}
            />
          </div>

          {/* Password Generator Section */}
          <div className={styles.generatorSection}>
            <button
              type="button"
              onClick={() => setShowGenerator(!showGenerator)}
              className={styles.generatorToggle}
            >
              {showGenerator ? '▼' : '▶'} Passwort-Generator
            </button>

            {showGenerator && (
              <div className={styles.generator}>
                <div className={styles.generatorControls}>
                  <div className={styles.lengthControl}>
                    <label htmlFor="length" className="form-label">
                      Länge: {generatorOptions.length}
                    </label>
                    <input
                      type="range"
                      id="length"
                      name="length"
                      min="12"
                      max="32"
                      value={generatorOptions.length}
                      onChange={handleGeneratorChange}
                      className={styles.slider}
                    />
                    <div className={styles.sliderLabels}>
                      <span>12</span>
                      <span>32</span>
                    </div>
                  </div>

                  <div className={styles.charTypeControls}>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        name="include_lowercase"
                        checked={generatorOptions.include_lowercase}
                        onChange={handleGeneratorChange}
                      />
                      <span>Kleinbuchstaben (a-z)</span>
                    </label>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        name="include_uppercase"
                        checked={generatorOptions.include_uppercase}
                        onChange={handleGeneratorChange}
                      />
                      <span>Großbuchstaben (A-Z)</span>
                    </label>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        name="include_numbers"
                        checked={generatorOptions.include_numbers}
                        onChange={handleGeneratorChange}
                      />
                      <span>Zahlen (0-9)</span>
                    </label>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        name="include_symbols"
                        checked={generatorOptions.include_symbols}
                        onChange={handleGeneratorChange}
                      />
                      <span>Sonderzeichen (!@#$...)</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    disabled={generatingPassword}
                    className="btn btn-secondary"
                  >
                    {generatingPassword ? 'Generieren...' : 'Passwort generieren'}
                  </button>
                </div>

                {generatedPassword && (
                  <div className={styles.generatedResult}>
                    <label className="form-label">Generiertes Passwort:</label>
                    <div className={styles.passwordBox}>
                      <input
                        type="text"
                        value={generatedPassword}
                        readOnly
                        className="form-input"
                      />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(generatedPassword)}
                        className="btn btn-secondary"
                      >
                        Kopieren
                      </button>
                    </div>

                    {(() => {
                      const strength = getPasswordStrength();
                      return strength ? (
                        <div className={styles.strengthIndicator}>
                          <span
                            className={styles.strengthBadge}
                            style={{ backgroundColor: strength.color }}
                          >
                            {strength.level} ({strength.entropy} bits)
                          </span>
                          <span className={styles.strengthDesc}>
                            {strength.description}
                          </span>
                        </div>
                      ) : null;
                    })()}

                    <div className={styles.actionButtons}>
                      <button
                        type="button"
                        onClick={useGeneratedPassword}
                        className="btn btn-primary"
                      >
                        Als SECRET verwenden
                      </button>
                      <button
                        type="button"
                        onClick={useAsPassphrase}
                        className="btn btn-secondary"
                      >
                        Als PASSWORT verwenden
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="ttl" className="form-label">
              Ablauf
            </label>
            <select
              id="ttl"
              name="ttl"
              value={formData.ttl}
              onChange={handleInputChange}
              className="form-select"
            >
              {getTTLOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="passphrase" className="form-label">
              Passwort (Optional)
            </label>
            <div className={styles.passphraseInputWrapper}>
              <input
                type={showPassphrase ? "text" : "password"}
                id="passphrase"
                name="passphrase"
                value={formData.passphrase}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Zusätzliche Absicherung (optional)"
                maxLength={128}
              />
              {formData.passphrase && (
                <button
                  type="button"
                  onClick={() => setShowPassphrase(!showPassphrase)}
                  className={styles.toggleVisibility}
                  aria-label={showPassphrase ? "Hide passphrase" : "Show passphrase"}
                >
                  {showPassphrase ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              )}
            </div>
            <small className="form-hint">
              Füge eine zusätzliche Absicherung hinzu. Empfänger brauchen dieses Passwort, um den Secret zu öffnen.
            </small>
          </div>

          <button
            type="submit"
            disabled={loading || !formData.secret.trim()}
            className="btn btn-primary"
          >
            {loading ? 'Erstellen...' : 'Secret erstellen'}
          </button>
        </form>
    </div>
  );
};

export default CreateSecretPage;